import { initReactI18next } from 'react-i18next';

import * as Platform from '@actual-app/core/shared/platform';
import i18n from 'i18next';

import spanish from './locales/es.json';

const BUILT_IN_PRODUCT_LOCALES = ['es', 'en'] as const;
type ProductLocale = (typeof BUILT_IN_PRODUCT_LOCALES)[number];
const PRODUCT_DESCRIPTIONS = {
  en: 'A local-first personal finance application that works offline.',
  es: 'Una aplicación de finanzas personales local que funciona sin conexión.',
} as const;

export const availableLanguages = Platform.isPlaywright
  ? []
  : [...BUILT_IN_PRODUCT_LOCALES];

const isLanguageAvailable = (language: string): language is ProductLocale =>
  BUILT_IN_PRODUCT_LOCALES.includes(language as ProductLocale);

void i18n
  .use(initReactI18next)
  .init({
    lng: 'en',
    supportedLngs: BUILT_IN_PRODUCT_LOCALES,
    resources: {
      en: { translation: {} },
      es: { translation: spanish },
    },

    // allow keys to be phrases having `:`, `.`
    nsSeparator: false,
    keySeparator: false,
    fallbackLng: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      transSupportBasicHtmlNodes: false,
    },
  });

const resolveLanguage = (language: string): ProductLocale | undefined => {
  // English is always available since we use natural-language keys.
  if (language === 'en') return 'en';

  if (isLanguageAvailable(language)) return language;

  const lowercaseLanguage = language.toLowerCase();
  if (lowercaseLanguage !== language) {
    console.info(
      `Unknown locale ${language}, falling back to ${lowercaseLanguage}`,
    );
    return resolveLanguage(lowercaseLanguage);
  }

  if (language.includes('-')) {
    const fallback = language.split('-')[0];
    console.info(`Unknown locale ${language}, falling back to ${fallback}`);
    return resolveLanguage(fallback);
  }

  return undefined;
};

export const setI18NextLanguage = (language: string | null) => {
  const defaultLanguages = Array.isArray(navigator.languages)
    ? navigator.languages
    : [navigator.language || 'en'];
  const languagesToTry = language ? [language] : defaultLanguages;

  let resolved: ProductLocale | undefined;

  for (const lang of languagesToTry) {
    resolved = resolveLanguage(lang);
    if (resolved) break;
  }

  if (!resolved) {
    // Fall back to English
    console.info(
      language
        ? `Unknown locale ${language}, falling back to en`
        : `Unknown locales [${languagesToTry.join(', ')}] falling back to en`,
    );
    resolved = 'en';
  }

  setDocumentLocale(resolved);

  if (resolved === i18n.language) {
    return; // language is already set
  }

  void i18n.changeLanguage(resolved);
};

function setDocumentLocale(language: ProductLocale) {
  document.documentElement.lang = language;

  document
    .querySelector<HTMLLinkElement>('link[rel="manifest"]')
    ?.setAttribute(
      'href',
      import.meta.env.BASE_URL +
        (language === 'es' ? 'site.es.webmanifest' : 'site.webmanifest'),
    );

  for (const selector of [
    'meta[name="description"]',
    'meta[property="og:description"]',
    'meta[name="twitter:description"]',
  ]) {
    document
      .querySelector<HTMLMetaElement>(selector)
      ?.setAttribute('content', PRODUCT_DESCRIPTIONS[language]);
  }
}
